import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiDelete } from '../../api/client';
import type { Client } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { PageHeader, type ActionDef } from '../../components/ui/PageHeader';
import { Skeleton } from '../../components/ui/Skeleton';
import { ConfirmDialog } from '../../components/ui/Modal';
import { clientFullName, formatDate } from '../../utils/format';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import ClientOverviewTab from './tabs/ClientOverviewTab';
import ClientEventsTab from './tabs/ClientEventsTab';

const TABS = [
  { id: 'overview', label: 'Anagrafica' },
  { id: 'events',   label: 'Eventi' },
];

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: client, isLoading } = useQuery<Client>({
    queryKey: ['client', id],
    queryFn: () => apiGet<Client>(`/clients/${id}`),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/clients/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Cliente eliminato');
      navigate('/clients');
    },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <Skeleton height={40} width="40%" />
        <Skeleton height={200} />
      </div>
    );
  }

  if (!client) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
        <p>Cliente non trovato.</p>
        <Button variant="ghost" onClick={() => navigate('/clients')} style={{ marginTop: '1rem' }}>
          ← Torna ai clienti
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        backTo="/clients"
        title={clientFullName(client)}
        metadata={
          <>
            {client.email && <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{client.email}</span>}
            {client.phone && (
              <>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>·</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{client.phone}</span>
              </>
            )}
            {client.city && (
              <>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>·</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{client.city}</span>
              </>
            )}
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>·</span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {client._count?.events ?? 0} eventi
            </span>
          </>
        }
        actions={[
          { label: 'Modifica', icon: <Pencil size={14} />, onClick: () => setActiveTab('overview') },
          ...(isAdmin ? [{ label: 'Elimina', icon: <Trash2 size={14} />, onClick: () => setShowDeleteConfirm(true), danger: true }] : []),
        ]}
      />

      {/* Tabs */}
      <div className="table-responsive" style={{
        display: 'flex',
        gap: '0.375rem',
        paddingBottom: '0.5rem',
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 1rem',
              background: activeTab === tab.id ? 'var(--color-primary-highlight)' : 'transparent',
              border: activeTab === tab.id ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
              borderRadius: 'var(--radius-interactive)',
              cursor: 'pointer',
              color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 'var(--text-sm)',
              fontWeight: activeTab === tab.id ? 600 : 400,
              transition: 'all var(--spring-duration) var(--spring-easing)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <ClientOverviewTab client={client} />}
      {activeTab === 'events'   && <ClientEventsTab client={client} />}

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Elimina cliente"
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        description={
          <>
            Stai per eliminare <strong>{clientFullName(client)}</strong>.
            <br /><br />
            Il cliente verrà disattivato. Gli eventi e i progetti associati resteranno nel sistema.
          </>
        }
      />
    </div>
  );
}
